/**
 * Lazy-loads translation messages for the given locale code.
 * Only the requested locale is downloaded, saving ~680KB from the initial bundle.
 * English (en-US) uses react-intl's defaultMessage — no JSON needed.
 *
 * Each locale JSON is split into a separate webpack chunk via dynamic import().
 * This means non-English users load only their ~60-80KB locale file on demand,
 * rather than all 10 locales being bundled eagerly.
 */
const translationLoaders: Record<
  string,
  () => Promise<Record<string, string>>
> = {
  'ja-JP': () =>
    import(/* webpackChunkName: "locale-ja-JP" */ './ja-JP.json').then(
      (m) => m.default
    ),
  'ko-KR': () =>
    import(/* webpackChunkName: "locale-ko-KR" */ './ko-KR.json').then(
      (m) => m.default
    ),
  'id-ID': () =>
    import(/* webpackChunkName: "locale-id-ID" */ './id-ID.json').then(
      (m) => m.default
    ),
  'es-ES': () =>
    import(/* webpackChunkName: "locale-es-ES" */ './es-ES.json').then(
      (m) => m.default
    ),
  'pt-BR': () =>
    import(/* webpackChunkName: "locale-pt-BR" */ './pt-BR.json').then(
      (m) => m.default
    ),
  'fr-FR': () =>
    import(/* webpackChunkName: "locale-fr-FR" */ './fr-FR.json').then(
      (m) => m.default
    ),
  'zh-CN': () =>
    import(/* webpackChunkName: "locale-zh-CN" */ './zh-CN.json').then(
      (m) => m.default
    ),
  'zh-TW': () =>
    import(/* webpackChunkName: "locale-zh-TW" */ './zh-TW.json').then(
      (m) => m.default
    ),
  'it-IT': () =>
    import(/* webpackChunkName: "locale-it-IT" */ './it-IT.json').then(
      (m) => m.default
    ),
  'es-419': () =>
    import(/* webpackChunkName: "locale-es-419" */ './es-419.json').then(
      (m) => m.default
    ),
};

const cache: Record<string, Record<string, string>> = {};

/**
 * Loads translations for a given locale code.
 * Returns undefined for English (react-intl uses defaultMessage).
 * Caches loaded translations to avoid re-fetching on locale switch.
 * Falls back to undefined (English) on network errors.
 */
export async function loadTranslations(
  localeCode: string
): Promise<Record<string, string> | undefined> {
  if (cache[localeCode]) return cache[localeCode];

  const loader = translationLoaders[localeCode];
  if (!loader) return undefined;

  try {
    const messages = await loader();
    cache[localeCode] = messages;
    return messages;
  } catch {
    // If loading fails (network error, CDN issue), fall back to English
    // eslint-disable-next-line no-console
    console.warn(
      `Failed to load translations for ${localeCode}, falling back to English`
    );
    return undefined;
  }
}

export default translationLoaders;
